// Talkani Plus — loyalty program landing & user dashboard widget.
// Public marketing page when not logged in; shows current tier + rewards
// progress when authenticated. Tier rules live in /api/loyalty/me.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Crown, Star, Gift, ArrowRight, Check, Trophy } from "lucide-react";
import SEOHead from "../components/SEOHead";

const TIERS = [
  { key: "bronze", label: "Bronze", min: 0, color: "#A8743D", icon: Star, perks: ["Welcome-Bonus 10€", "Persönlicher Account"] },
  { key: "silver", label: "Silber", min: 3, color: "#9CA3AF", icon: Star, perks: ["Bronze-Perks", "5% Dauerrabatt", "Prioritäts-Support"] },
  { key: "gold", label: "Gold", min: 6, color: "#D4AF37", icon: Crown, perks: ["Silber-Perks", "10% Dauerrabatt", "1 Gratis-Lieferung / Monat"] },
  { key: "platinum", label: "Platin", min: 12, color: "#7C3AED", icon: Trophy, perks: ["Gold-Perks", "15% Dauerrabatt", "Persönlicher Concierge", "VIP-Express 60 Min"] },
];

export default function LoyaltyPage() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    api.get("/loyalty/me").then((r) => setStatus(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [user]);

  const currentTierIdx = status ? Math.max(0, TIERS.findIndex((t) => t.key === status.tier)) : 0;
  const currentTier = TIERS[currentTierIdx];
  const nextTier = TIERS[currentTierIdx + 1];

  return (
    <div data-testid="loyalty-page">
      <SEOHead
        title="Talkani Plus — Treueprogramm | Bis zu 15% Dauerrabatt"
        description="Werden Sie Talkani Plus Mitglied — sammeln Sie Buchungen, sichern Sie sich Dauerrabatte bis 15% und exklusive Vorteile."
      />

      <section className="relative bg-[#090A0B] text-white py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF5A00]/30 via-transparent to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-2 text-[#FF5A00] mb-4">
            <Crown size={16} />
            <span className="text-xs font-bold tracking-[0.3em] uppercase">Talkani Plus</span>
          </div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter mb-6 leading-[0.95]">
            Mehr buchen.<br />Mehr sparen.
          </h1>
          <p className="text-lg sm:text-xl text-neutral-300 max-w-2xl mb-10 leading-relaxed">
            Unser Treueprogramm belohnt regelmäßige Kunden mit Dauerrabatten, Gratis-Lieferungen und VIP-Support.
            Kostenlos. Automatisch. Ab der ersten Buchung.
          </p>
          {!user && (
            <Link
              to="/login"
              className="inline-flex items-center gap-2 bg-[#FF5A00] hover:bg-[#E04F00] text-white font-bold uppercase tracking-wide px-7 py-4 transition rounded-sm"
              data-testid="loyalty-join-btn"
            >
              Jetzt Mitglied werden <ArrowRight size={16} />
            </Link>
          )}
        </div>
      </section>

      {/* Current status — logged-in only */}
      {user && (
        <section className="py-16 lg:py-20 bg-white">
          <div className="max-w-5xl mx-auto px-6 lg:px-12">
            {loading && <div className="text-neutral-500" data-testid="loyalty-loading">Laden…</div>}
            {status && (
              <>
                <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#FF5A00] mb-3">Ihr Status</p>
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: currentTier.color }}>
                    <currentTier.icon size={28} className="text-white" />
                  </div>
                  <div>
                    <div className="text-3xl font-black tracking-tight">{currentTier.label}</div>
                    <div className="text-sm text-neutral-500">{status.completed_bookings} abgeschlossene Buchungen</div>
                  </div>
                </div>

                {nextTier && (
                  <div className="border border-neutral-200 p-6 mb-10" data-testid="loyalty-progress">
                    <div className="flex justify-between text-xs font-bold uppercase tracking-widest text-neutral-500 mb-3">
                      <span>Fortschritt zu {nextTier.label}</span>
                      <span>{status.completed_bookings} / {nextTier.min}</span>
                    </div>
                    <div className="h-2 bg-neutral-100 overflow-hidden">
                      <div
                        className="h-full bg-[#FF5A00] transition-all"
                        style={{ width: `${Math.min(100, (status.completed_bookings / nextTier.min) * 100)}%` }}
                      />
                    </div>
                    <p className="mt-4 text-sm text-neutral-600">
                      Noch <strong>{Math.max(0, nextTier.min - status.completed_bookings)} Buchung(en)</strong> bis zum {nextTier.label}-Status.
                    </p>
                  </div>
                )}

                {status.active_discount_code && (
                  <div className="bg-orange-50 border border-orange-200 p-5 mb-10 flex items-center gap-4" data-testid="loyalty-active-code">
                    <Gift size={28} className="text-[#FF5A00] flex-shrink-0" />
                    <div>
                      <div className="font-black tracking-tight text-sm uppercase">Aktiver Bonus-Code</div>
                      <div className="font-mono text-lg font-black text-[#FF5A00] tracking-wider">{status.active_discount_code}</div>
                      <div className="text-xs text-neutral-600 mt-1">Wird automatisch bei Ihrer nächsten Buchung berücksichtigt.</div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      )}

      {/* Tier overview */}
      <section className="py-20 lg:py-28 bg-[#F8F9FA] border-y border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter mb-12">Vier Stufen. Echte Vorteile.</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {TIERS.map((tier) => (
              <div
                key={tier.key}
                className={`bg-white border-2 p-6 ${user && status?.tier === tier.key ? "border-[#FF5A00]" : "border-neutral-200"}`}
                data-testid={`loyalty-tier-${tier.key}`}
              >
                <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ background: tier.color }}>
                  <tier.icon size={20} className="text-white" />
                </div>
                <div className="text-xs font-bold tracking-widest uppercase text-neutral-500 mb-1">Ab {tier.min} Buchungen</div>
                <div className="text-2xl font-black tracking-tight mb-4">{tier.label}</div>
                <ul className="space-y-2">
                  {tier.perks.map((p) => (
                    <li key={p} className="flex items-start gap-2 text-sm text-neutral-700">
                      <Check size={14} className="text-[#FF5A00] flex-shrink-0 mt-0.5" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#090A0B] text-white text-center">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-3xl sm:text-4xl font-black tracking-tighter mb-6">Bereit loszulegen?</h2>
          <Link
            to="/book"
            className="inline-flex items-center gap-2 bg-[#FF5A00] hover:bg-[#E04F00] text-white font-bold uppercase tracking-wide px-7 py-4 transition rounded-sm"
            data-testid="loyalty-cta-book"
          >
            Erste Buchung starten <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
